import type { CompositionToken, WordToken } from "./schema";
import { alignmentKey, longestCommonSubsequence } from "./tokenize";

const SENTENCE_END = /[.!?]/;

export const endsSentence = (token: CompositionToken): boolean =>
  token.kind === "punct" && SENTENCE_END.test(token.text);

/** Token ids grouped into sentences, each closed by its ending punctuation. */
export function sentences(tokens: readonly CompositionToken[]): string[][] {
  return tokens
    .reduce<string[][]>(
      (groups, token) => {
        const open = groups[groups.length - 1] ?? [];
        const closed = groups.slice(0, -1);
        const grown = [...open, token.id];
        return endsSentence(token)
          ? [...closed, grown, []]
          : [...closed, grown];
      },
      [[]],
    )
    .filter((group) => group.length > 0);
}

export type SpanReplacement =
  | {
      readonly outcome: "replaced";
      /** The span, widened over any neighbour split off the same word. */
      readonly spanIds: readonly string[];
      readonly tokens: readonly CompositionToken[];
    }
  | { readonly outcome: "same-word" }
  | { readonly outcome: "unaligned" };

export interface SpanReplacementOptions {
  /**
   * The recordings a word and the words it replaced were heard in; by
   * default the word's own. `recordingsOf` reads them from the edit log.
   */
  readonly recordingsOf?: (word: WordToken) => ReadonlySet<string>;
  /**
   * Every text recognition gave the span and the words it replaced; a
   * result matching any of them is `same-word`, not only the span's own.
   */
  readonly rejected?: readonly string[];
}

const isSpoken = (t: CompositionToken): boolean => t.kind !== "punct";

const ownRecording = (word: WordToken): ReadonlySet<string> =>
  new Set(word.asr === null ? [] : [word.asr.dictationId]);

const shareAny = (a: ReadonlySet<string>, b: ReadonlySet<string>): boolean =>
  [...a].some((id) => b.has(id));

/**
 * Where the re-said sentence that goes on from `from` ends: its first
 * sentence-ending punctuation after a word, else the end of the re-say.
 */
const sentenceEndFrom = (
  reSaid: readonly CompositionToken[],
  from: number,
): number => {
  const firstWord = reSaid.findIndex((t, i) => i >= from && isSpoken(t));
  const end =
    firstWord < 0
      ? -1
      : reSaid.findIndex((t, i) => i > firstWord && endsSentence(t));
  return end < 0 ? reSaid.length : end;
};

/** Positions among the spoken tokens whose next spoken token follows directly. */
const touchingNext = (sentence: readonly CompositionToken[]): Set<number> => {
  const places = sentence.flatMap((t, i) => (isSpoken(t) ? [i] : []));
  return new Set(
    places.flatMap((place, k) => (places[k + 1] === place + 1 ? [k] : [])),
  );
};

/** Old words at each end of the gap: kept, or joined to the span. */
interface Ends {
  readonly before: number;
  readonly after: number;
}

interface Neighbourhood {
  /** The sentence's words and slots, in order. */
  readonly words: readonly CompositionToken[];
  /** Positions in `words` with no punctuation before the next word. */
  readonly touching: ReadonlySet<number>;
  readonly recordingsOf: (word: WordToken) => ReadonlySet<string>;
}

const isDoubted = (t: CompositionToken | undefined): boolean =>
  t?.kind === "word" && t.resolution === "surfaced";

/**
 * The fewest neighbours, `needed` in all, to re-say with the span when
 * recognition split one word in two ("lemon zine"). Only the kept words
 * between the anchors and the span are candidates, so none was said again
 * in the re-say, and each joins as another piece of the same word:
 * unconfirmed, touching the span or a joined neighbour, and heard in a
 * recording that one was heard in. Null when too few qualify.
 */
function joinNeighbours(
  around: Neighbourhood,
  span: { readonly start: number; readonly end: number },
  kept: Ends,
  needed: number,
): Ends | null {
  const { words, touching, recordingsOf } = around;
  const joins = (at: number, inner: number): boolean => {
    const neighbour = words[at];
    const border = words[inner];
    return (
      neighbour?.kind === "word" &&
      neighbour.resolution !== "confirmed" &&
      border?.kind === "word" &&
      touching.has(Math.min(at, inner)) &&
      shareAny(recordingsOf(neighbour), recordingsOf(border))
    );
  };
  const candidates = (end: keyof Ends, step: 1 | -1, from: number) => {
    const outward = Array.from(
      { length: kept[end] },
      (_, d) => from + step * d,
    );
    const stop = outward.findIndex((at) => !joins(at, at - step));
    return outward
      .slice(0, stop < 0 ? outward.length : stop)
      .map((at, distance) => ({
        end,
        distance,
        doubted: isDoubted(words[at]),
      }));
  };
  // Nearest first; at equal distance a doubted neighbour is likelier the
  // other half than one recognition was sure of, then the earlier word.
  const taken = [
    ...candidates("before", -1, span.start - 1),
    ...candidates("after", 1, span.end + 1),
  ]
    .sort(
      (a, b) =>
        a.distance - b.distance ||
        Number(b.doubted) - Number(a.doubted) ||
        (a.end === "before" ? -1 : 1),
    )
    .slice(0, needed);
  return taken.length < needed
    ? null
    : {
        before: taken.filter((c) => c.end === "before").length,
        after: taken.filter((c) => c.end === "after").length,
      };
}

/**
 * What a re-said sentence puts in place of the surfaced span, and nothing
 * else. Words outside the span anchor the alignment and stay as they were;
 * of the new words between the anchors, the span gets those left once each
 * old non-span word in that gap has its own. With none left over, the span
 * takes in the neighbours recognition split off the same word, as few as
 * leave it a word; failing that the result is `unaligned`. Nothing past
 * the end of the re-said sentence is ever part of it. A result equal to
 * the span, or to anything in `rejected`, is `same-word`, so a rejected
 * word is never offered back as a new one.
 */
export function spanReplacement(
  sentence: readonly CompositionToken[],
  spanIds: readonly string[],
  reSaid: readonly CompositionToken[],
  options: SpanReplacementOptions = {},
): SpanReplacement {
  const span = new Set(spanIds);
  const oldWords = sentence.filter(isSpoken);
  const spokenAt = reSaid.flatMap((t, i) => (isSpoken(t) ? [i] : []));
  const newWords = reSaid.filter(isSpoken);
  // Span words and text-free slots must never anchor, so each gets a key no
  // other word can have.
  const keyOf = (t: CompositionToken, i: number, side: string): string =>
    t.kind === "word" && !span.has(t.id)
      ? alignmentKey(t.text)
      : `\u0000${side}${i}`;
  const pairs = longestCommonSubsequence(
    oldWords.map((t, i) => keyOf(t, i, "old")),
    newWords.map((t, i) => keyOf(t, i, "new")),
  );
  const spanPositions = oldWords.flatMap((t, i) => (span.has(t.id) ? [i] : []));
  const spanStart = spanPositions[0];
  const spanEnd = spanPositions[spanPositions.length - 1];
  if (spanStart === undefined || spanEnd === undefined) {
    return { outcome: "unaligned" };
  }
  const left = [...pairs].reverse().find(([o]) => o < spanStart);
  const right = pairs.find(([o]) => o > spanEnd);
  const from = left === undefined ? 0 : (spokenAt[left[1]] ?? -1) + 1;
  // The span lies inside one sentence, so a sentence the child went on to
  // say is not part of it, even with no anchor after the span to stop at.
  const to = Math.min(
    right === undefined ? reSaid.length : (spokenAt[right[1]] ?? reSaid.length),
    sentenceEndFrom(reSaid, from),
  );
  // Old words between an anchor and the span were re-said too, in their own
  // places, so as many new words at each end belong to them, not the span.
  const kept: Ends = {
    before: spanStart - (left === undefined ? 0 : left[0] + 1),
    after: (right === undefined ? oldWords.length : right[0]) - spanEnd - 1,
  };
  const between = reSaid.slice(from, to);
  const spokenBetween = between.flatMap((t, i) => (isSpoken(t) ? [i] : []));
  const room = spokenBetween.length - kept.before - kept.after;
  const joined =
    room > 0
      ? { before: 0, after: 0 }
      : joinNeighbours(
          {
            words: oldWords,
            touching: touchingNext(sentence),
            recordingsOf: options.recordingsOf ?? ownRecording,
          },
          { start: spanStart, end: spanEnd },
          kept,
          1 - room,
        );
  if (joined === null) return { outcome: "unaligned" };
  const firstSpoken = spokenBetween[kept.before - joined.before];
  const lastSpoken =
    spokenBetween[spokenBetween.length - 1 - (kept.after - joined.after)];
  if (
    firstSpoken === undefined ||
    lastSpoken === undefined ||
    firstSpoken > lastSpoken
  ) {
    return { outcome: "unaligned" };
  }
  const replacement = between.slice(firstSpoken, lastSpoken + 1);
  const joinedBefore = oldWords.slice(spanStart - joined.before, spanStart);
  const joinedAfter = oldWords.slice(spanEnd + 1, spanEnd + 1 + joined.after);
  const replaced = [
    ...joinedBefore,
    ...oldWords.filter((t) => span.has(t.id)),
    ...joinedAfter,
  ];

  const keys = (tokens: readonly CompositionToken[]) =>
    tokens
      .map((t) =>
        t.kind === "word" ? alignmentKey(t.text) : `\u0000${t.kind}`,
      )
      .filter((k) => k !== "\u0000punct")
      .join(" ");
  const textKeys = (text: string) =>
    text
      .split(/\s+/)
      .map(alignmentKey)
      .filter((k) => k !== "")
      .join(" ");
  const turnedDown = new Set([
    keys(replaced),
    ...(options.rejected ?? []).map(textKeys),
  ]);
  return turnedDown.has(keys(replacement))
    ? { outcome: "same-word" }
    : {
        outcome: "replaced",
        spanIds: [
          ...joinedBefore.map((t) => t.id),
          ...spanIds,
          ...joinedAfter.map((t) => t.id),
        ],
        tokens: replacement,
      };
}
